import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@store/index';

/**
 * Shared request-action flow state (feature 107).
 *
 * The Bookings and Dashboard screens share an identical delete/cancel flow that previously
 * lived as scattered component-local state (target event, in-flight flag, error) plus an
 * active selection. Centralizing it here lets both screens drive the flow through reducers
 * and read a single source of truth (FR-016).
 */

export type RequestActionKind = 'delete' | 'cancel';

export interface RequestActionsState {
  /** The action currently in progress, or `null` when idle. */
  kind: RequestActionKind | null;
  /** The event the in-progress action targets. */
  targetEventId: string | null;
  /** Whether the targeted action is awaiting completion. */
  isInFlight: boolean;
  /** Last action error message, or `null`. */
  error: string | null;
  /** The currently selected/active event id (e.g., opened detail). */
  selectedEventId: string | null;
}

const initialState: RequestActionsState = {
  kind: null,
  targetEventId: null,
  isInFlight: false,
  error: null,
  selectedEventId: null,
};

export interface BeginRequestActionPayload {
  kind: RequestActionKind;
  targetEventId: string;
}

export const requestActionsSlice = createSlice({
  name: 'requestActions',
  initialState,
  reducers: {
    beginRequestAction: (state, action: PayloadAction<BeginRequestActionPayload>) => {
      state.kind = action.payload.kind;
      state.targetEventId = action.payload.targetEventId;
      state.isInFlight = true;
      state.error = null;
    },
    requestActionSucceeded: (state) => {
      state.kind = null;
      state.targetEventId = null;
      state.isInFlight = false;
      state.error = null;
    },
    requestActionFailed: (state, action: PayloadAction<string>) => {
      state.isInFlight = false;
      state.error = action.payload;
    },
    clearRequestAction: () => initialState,
    setSelectedEventId: (state, action: PayloadAction<string | null>) => {
      state.selectedEventId = action.payload;
    },
  },
});

export const {
  beginRequestAction,
  requestActionSucceeded,
  requestActionFailed,
  clearRequestAction,
  setSelectedEventId,
} = requestActionsSlice.actions;

export const requestActionsReducer = requestActionsSlice.reducer;

export const selectRequestActionKind = (state: RootState): RequestActionKind | null =>
  state.requestActions.kind;
export const selectRequestActionTargetEventId = (state: RootState): string | null =>
  state.requestActions.targetEventId;
export const selectRequestActionInFlight = (state: RootState): boolean =>
  state.requestActions.isInFlight;
export const selectRequestActionError = (state: RootState): string | null =>
  state.requestActions.error;
export const selectSelectedEventId = (state: RootState): string | null =>
  state.requestActions.selectedEventId;
